import type { Action } from '@reduxjs/toolkit';
import type { Dispatch, Store } from 'redux';
import { getStore } from './store/getStore.ts';

type StoreOrGetStore = Store|(() => Store);

interface TypedAction extends Action {
  type: string;
}

function isStore(maybeStore: unknown): maybeStore is Store {
  return Boolean(maybeStore && typeof maybeStore === 'object' && 'dispatch' in maybeStore);
}

/**
 * Creates an action method.
 * @example
 * const action = createAction(() => dispatch => dispatch({ type: 'test' }));
 * action(); // dispatches with getStore()
 * action(store); // dispatches with store
 */
export function createAction<TArgs extends unknown[], TAction extends TypedAction>(
  createActionMethod: (...args: TArgs) => ((dispatch: Dispatch<TAction>) => Promise<void> | void)
) {
  return async (...args: [...TArgs, storeOrGetStore?: StoreOrGetStore]) => {
    const lastArg: StoreOrGetStore | unknown = args[args.length - 1];
    let methodArgs: TArgs;
    let store: Store;
    let maybeStore: unknown;

    if (isStore(lastArg)) {
      store = lastArg;
      methodArgs = args.slice(-1) as TArgs;
    } else if (typeof lastArg === 'function' && isStore(maybeStore = lastArg())) {
      store = maybeStore;
      methodArgs = args.slice(-1) as TArgs;
    } else {
      store = getStore();
      methodArgs = args as unknown as TArgs;
    }

    await  createActionMethod(...methodArgs)(store.dispatch);
  }
}